import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
// Top-level barrel only — wildcard sub-paths (`@ui-nuvertos/shared/modules`)
// can't be resolved at runtime when @ui-nuvertos/shared lives in shell's
// federation `sharedMappings`.
import { TenantModuleService } from '@ui-nuvertos/shared';
import { ToasterService } from './toaster.service';

/**
 * Blocks navigation to top-level routes whose `data.module_key` is not enabled
 * for the current tenant (per `TenantModuleService`).
 *
 * How it decides:
 *   1. Walk up `route.parent` until `data.module_key` is found, or root is hit.
 *   2. No `module_key` → ungated route (e.g. `/dashboard`, `/clinical`,
 *      `/appointments`, `/unauthorized`) → allow.
 *   3. `isEnabled(key) === true` → allow.
 *   4. Otherwise: toast + redirect to `/dashboard` and deny.
 *
 * Strictly additive — does not touch `data.module_id` / `sub_module_id` or the
 * existing `PermissionGuard`. Stack both on the same route when required.
 */
@Injectable({ providedIn: 'root' })
export class ModuleEnabledGuard implements CanActivate {
  private readonly tenantModules = inject(TenantModuleService);
  private readonly router = inject(Router);
  private readonly toaster = inject(ToasterService);

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean {
    const moduleKey = this.resolveModuleKey(route);
    if (!moduleKey) {
      return true;
    }
    // Federation note: when this guard runs from a federated bundle (e.g.
    // libs/auth loaded via the shell's sharedMappings), it gets its OWN
    // TenantModuleService instance whose APP_INITIALIZER never ran.
    // modules() is empty in that case — fall back to allow, trusting the
    // host (shell) to have populated sidebar from its initialised instance.
    // Standalone hosts (accounting) populate modules() via their own
    // APP_INITIALIZER and the check works as intended.
    const enabledModules = this.tenantModules.modules();
    if (!enabledModules.length) {
      return true;
    }
    if (this.tenantModules.isEnabled(moduleKey)) {
      return true;
    }

    const displayName = enabledModules.find((m) => m.key === moduleKey)?.name ?? moduleKey;
    this.toaster.error(
      'Module not enabled',
      `'${displayName}' is not enabled for your tenant. Contact your administrator.`,
    );
    this.router.navigate(['/dashboard']);
    return false;
  }

  private resolveModuleKey(route: ActivatedRouteSnapshot | null): string | null {
    for (let r = route; r; r = r.parent) {
      const key = r.data?.['module_key'];
      if (typeof key === 'string' && key.length > 0) {
        return key;
      }
    }
    return null;
  }
}
